import { Marker, Popup, useMap } from 'react-map-gl/maplibre';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { APIResponseAcknowledgement } from './APIAcknowledgeService';
import style from './AcknowledgementMap.module.css';

// Draws numbered circles per warning id. Use it as a child of <LocationMap>.

const CLUSTER_PX = 40;      // points closer than this (screen pixels) join one circle
const CIRCLE_SIZE = 30;

// same warning id always gets the same colour (also after reload)
const colorFor = (id: string) => {
    let h = 0;
    for (const ch of id ?? "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return `hsl(${h % 360}, 70%, 50%)`;
};

type AckCluster = {
    key: string;
    ewId: string;
    lng: number;
    lat: number;
    items: APIResponseAcknowledgement[];
};

type AcknowledgementLayerProps = {
    data: APIResponseAcknowledgement[];
};

const AcknowledgementLayer = ({ data }: AcknowledgementLayerProps) => {
    const { current: map } = useMap();      // the parent <Map> from LocationMap

    const [clusters, setClusters] = useState<AckCluster[]>([]);
    const [hover, setHover] = useState<AckCluster | null>(null);

    // group by warning id
    const grouped = useMemo(() => {
        const g = new Map<string, APIResponseAcknowledgement[]>();
        data.forEach(item => {
            const list = g.get(item.ewId) ?? [];
            list.push(item);
            g.set(item.ewId, list);
        });
        return g;
    }, [data]);

    // cluster each warning's points by screen distance
    const buildClusters = useCallback(() => {
        if (!map) return;

        const out: AckCluster[] = [];
        grouped.forEach((items, ewId) => {
            const local: { x: number; y: number; sumLng: number; sumLat: number; items: APIResponseAcknowledgement[] }[] = [];

            items.forEach(item => {
                const lat = Number(item.lat);
                const lng = Number(item.lon);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

                const p = map.project([lng, lat]);
                let c = local.find(c => Math.hypot(c.x - p.x, c.y - p.y) <= CLUSTER_PX);
                if (!c) {
                    c = { x: p.x, y: p.y, sumLng: 0, sumLat: 0, items: [] };
                    local.push(c);
                }
                c.items.push(item);
                c.sumLng += lng;
                c.sumLat += lat;
            });

            local.forEach((c, i) => out.push({
                key: `${ewId}-${i}`,
                ewId,
                lng: c.sumLng / c.items.length,
                lat: c.sumLat / c.items.length,
                items: c.items,
            }));
        });

        setClusters(out);
        setHover(null);
    }, [map, grouped]);

    // build now, and again after every zoom
    useEffect(() => {
        if (!map) return;
        const m = map.getMap();
        const run = () => buildClusters();

        if (m.loaded()) run();
        else m.once('load', run);

        m.on('zoomend', run);
        return () => {
            m.off('zoomend', run);
            m.off('load', run);
        };
    }, [map, buildClusters]);

    // status -> count for the popup
    const statusCounts = (items: APIResponseAcknowledgement[]) => {
        const s = new Map<string, number>();
        items.forEach(i => {
            const k = (i.status ?? "unknown").toString().toLowerCase();
            s.set(k, (s.get(k) ?? 0) + 1);
        });
        return [...s];
    };

    return (
        <>
            {clusters.map(c => (
                <Marker key={c.key} longitude={c.lng} latitude={c.lat} anchor="center"
                        style={{ zIndex: hover?.key === c.key ? 2 : 1 }}>
                    <div
                        className={style.Circle}
                        style={{ background: colorFor(c.ewId), width: CIRCLE_SIZE, height: CIRCLE_SIZE }}
                        onMouseEnter={() => setHover(c)}
                        onMouseLeave={() => setHover(null)}
                    >
                        {c.items.length}
                    </div>
                </Marker>
            ))}

            {hover && (
                <Popup
                    className={style.AckPopup}
                    longitude={hover.lng}
                    latitude={hover.lat}
                    anchor="left"
                    offset={[CIRCLE_SIZE / 2 + 6, 0]}
                    closeButton={false}
                    closeOnClick={false}
                    maxWidth="360px"
                >
                    <div className={style.Title}>Warning {hover.ewId}</div>
                    <div className={style.Divider} />
                    <div className={style.Grid}>
                        <span className={style.Label}>Warning ID:</span>
                        <span className={style.Value}>{hover.ewId}</span>
                        <span className={style.Label}>Count:</span>
                        <span className={style.Value}>{hover.items.length}</span>
                        {statusCounts(hover.items).map(([status, n]) => (
                            <React.Fragment key={status}>
                                <span className={style.Label}>{status.charAt(0).toUpperCase() + status.slice(1)}:</span>
                                <span className={style.Value}>{n}</span>
                            </React.Fragment>
                        ))}
                    </div>
                </Popup>
            )}
        </>
    );
};

export default AcknowledgementLayer;
